import { Clock, Layers, MessageSquare } from "lucide-react"

import { cn } from "@/lib/utils"

import type { QueueRow } from "./queue-data"
import {
  isReviewStatus,
  reviewStatusLabel,
  type ReviewStatus,
  type WireReviewStatus,
} from "./queue-review-status"

type Tone = "info" | "print" | "people" | "master" | "err" | "muted"

/**
 * Each review status's tone, following the legacy queue's `statusTone` with the
 * redesign's tokens: escalation (Needs SME) its own colour; Needs an answer amber
 * like In review — nothing is wrong, someone just has to answer; Stopped muted, as
 * it wants nobody's attention; Failed the error tone.
 */
const STATUS_TONE: Record<ReviewStatus, Tone> = {
  pending: "info",
  running: "info",
  completed: "print",
  needs_input: "print",
  cancelled: "muted",
  needs_sme: "people",
  verified: "master",
  failed: "err",
}

const BADGE: Record<Tone, string> = {
  info: "bg-info-tint text-info ring-info/20",
  print: "bg-print-tint text-print ring-print/20",
  people: "bg-people-tint text-people ring-people/20",
  master: "bg-master-tint text-master ring-master/20",
  err: "bg-err-tint text-err ring-err/20",
  muted: "bg-surface-sunken text-text-3 ring-border",
}

const DOT: Record<Tone, string> = {
  info: "bg-info",
  print: "bg-print",
  people: "bg-people",
  master: "bg-master",
  err: "bg-err",
  muted: "bg-text-3",
}

/** The status pill, with the overnight marker beneath it when the version is parked. */
export function StatusCell({
  status,
  stage,
}: {
  status: WireReviewStatus
  stage: string | null
}): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <StatusPill status={status} />
      <OvernightMarker stage={stage} />
    </div>
  )
}

/**
 * Says which kind of "running" a review is in, beside the badge that cannot (legacy's
 * `OvernightMarker`). A review parked in the batch lane is `running` for hours, so on status
 * alone it looks like one about to finish.
 */
export function OvernightMarker({ stage }: { stage: string | null }): JSX.Element | null {
  if (stage !== "batch_wait") return null
  return (
    // Sized and set like the status pill above it, so the two read as one pair.
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-1.5 py-px",
        "text-[10px] font-medium leading-4 tracking-normal",
        "bg-print-tint text-print ring-1 ring-inset ring-print/20",
      )}
      title="Running overnight in a batch, at about half the cost — open it to run it now instead"
    >
      <Clock className="size-2.5" aria-hidden="true" />
      Overnight
    </span>
  )
}

export function StatusPill({ status }: { status: WireReviewStatus }): JSX.Element {
  // A status the queue does not know yet is neutral: nothing about it says good or bad.
  const tone = isReviewStatus(status) ? STATUS_TONE[status] : "muted"
  return (
    <span
      className={cn(
        "inline-flex min-w-[84px] items-center justify-center gap-1 rounded-full px-2 py-px",
        "text-[10px] font-medium leading-4 ring-1 ring-inset",
        BADGE[tone],
      )}
    >
      <span className={cn("size-1 rounded-full", DOT[tone])} aria-hidden="true" />
      {reviewStatusLabel(status)}
    </span>
  )
}

export function ProductCell({ row }: { row: QueueRow }): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <Layers className="size-4 shrink-0 text-text-3" aria-hidden="true" />
        {/* Said plainly when the extractor found no name, rather than printing the reg. no.
            twice, once as the title and again beneath it (as legacy does). */}
        {row.productName ? (
          <span className="truncate text-body font-medium text-text">{row.productName}</span>
        ) : (
          <span className="truncate text-body italic text-text-3">Product not yet identified</span>
        )}
        <span className="shrink-0 rounded-full border border-border-soft bg-surface-sunken px-1.5 font-id text-caption leading-5 text-text-3">
          v{row.versions}
        </span>
      </div>
      <div className="mt-0.5 flex items-center gap-2 font-id text-caption tabular-nums text-text-3">
        <span>EPA {row.epaRegNo}</span>
        {/* Internal reviews have no engagement, so nothing follows the reg. no. */}
        {row.clientName && (
          <>
            <span aria-hidden="true">·</span>
            <span className="truncate font-ui not-italic">{row.clientName}</span>
          </>
        )}
      </div>
    </div>
  )
}

export function FindingsCell({ row }: { row: QueueRow }): JSX.Element {
  // Not "No findings yet": the counts are held back, not empty (legacy says the same).
  if (!row.findingsReleased) {
    return <span className="text-body-sm text-text-3">Findings not released yet</span>
  }
  if (row.findingsOutstanding === 0 && row.findingsCorrected === 0) {
    return <span className="text-body-sm text-text-3">No findings yet</span>
  }
  return (
    <div className="flex flex-wrap items-baseline justify-center gap-x-2 text-center text-body-sm">
      <span className="text-text">
        <span className="font-id tabular-nums font-semibold">{row.findingsOutstanding}</span>{" "}
        outstanding
      </span>
      <span className="text-text-3">·</span>
      <span className="text-text-3">
        <span className="font-id tabular-nums">{row.findingsCorrected}</span> corrected
      </span>
    </div>
  )
}

/**
 * Unread messages, as the legacy queue shows them: a message icon with a red
 * count bubble on its corner ("9+" past nine). Nothing at all when there is
 * nothing unread, so the column only catches the eye where there is something
 * to read.
 */
export function CommsCell({ unread }: { unread: number }): JSX.Element | null {
  if (unread === 0) return null
  return <UnreadIcon unread={unread} />
}

/** The icon with its bubble. `unread` of 0 draws the bare icon (used on version rows). */
export function UnreadIcon({ unread }: { unread: number }): JSX.Element {
  const label = `${String(unread)} unread message${unread === 1 ? "" : "s"}`
  return (
    <span
      className="relative inline-flex"
      role="img"
      aria-label={unread > 0 ? label : "No unread messages"}
    >
      <MessageSquare className="size-4 text-text-3" aria-hidden="true" />
      {unread > 0 && (
        <span
          aria-hidden="true"
          className={cn(
            "absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center",
            "rounded-full bg-err px-1 text-[10px] font-semibold leading-none text-white",
            "ring-2 ring-surface",
          )}
        >
          {unread > 9 ? "9+" : unread}
        </span>
      )}
    </span>
  )
}
